import { ChecklistItemInDto, InspectionTemplateInDto } from 'shared/dtos/in/template';
import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';
import { Fetcher } from '../fetcher';
import {
  InspectionTemplateHttpRequestModel,
  InspectionTemplateHttpRestoreRequestModel,
} from './types';

export class InspectionTemplateRequestRunner implements IHttpRequestRunner {
  constructor(
    private fetcher: typeof Fetcher,
    private updateDeleted: (
      templateId: string,
      deleted: boolean
    ) => Promise<unknown>
  ) {}

  async execute(
    request: InspectionTemplateHttpRequestModel
  ): Promise<InspectionTemplateInDto | ChecklistItemInDto | ChecklistItemInDto[] | boolean | unknown> {
    switch (request.method) {
      case 'POST': {
        return this.create(request);
      }
      case 'PUT': {
        return this.edit(request);
      }
      case 'DELETE': {
        return this.remove(request);
      }
      case 'GET': {
        return this.restore(request as InspectionTemplateHttpRestoreRequestModel);
      }
    }
  }

  private async create(
    request: InspectionTemplateHttpRequestModel
  ): Promise<InspectionTemplateInDto | ChecklistItemInDto[]> {
    if (request.method !== 'POST') {
      throw new Error('Invalid method for inspection template create');
    }

    if ('inspectionTemplateOutDto' in request.data) {
      return this.fetcher.POST(
        request.url,
        request.data.inspectionTemplateOutDto
      ) as Promise<InspectionTemplateInDto>;
    }

    return this.fetcher.POST(
      request.url,
      request.data.checklistItemsOutDto
    ) as Promise<ChecklistItemInDto[]>;
  }

  private async edit(
    request: InspectionTemplateHttpRequestModel
  ): Promise<InspectionTemplateInDto | ChecklistItemInDto> {
    if (request.method !== 'PUT') {
      throw new Error('Invalid method for inspection template edit');
    }

    if ('inspectionTemplateOutDto' in request.data) {
      return this.fetcher.PUT(
        request.url,
        request.data.inspectionTemplateOutDto
      ) as Promise<InspectionTemplateInDto>;
    }

    if ('reorderDto' in request.data) {
      return this.fetcher.PUT(
        request.url,
        request.data.reorderDto
      ) as Promise<ChecklistItemInDto>;
    }

    return this.fetcher.PUT(
      request.url,
      request.data.checklistItemOutDto
    ) as Promise<ChecklistItemInDto>;
  }

  private async remove(
    request: InspectionTemplateHttpRequestModel
  ): Promise<unknown> {
    if (request.method !== 'DELETE') {
      throw new Error('Invalid method for inspection template remove');
    }

    return this.fetcher.DELETE(request.url);
  }

  private async restore(
    request: InspectionTemplateHttpRestoreRequestModel
  ): Promise<boolean> {
    await this.fetcher.GET(request.url);
    await this.updateDeleted(request.data.templateId, false);
    return true;
  }
}
